import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import useNotificationPreferencesFeatureFlag from './useNotificationPreferencesFeatureFlag';
import { useAuth0User } from '@hooks/useAuth0User';

/**
 * Fetch notification preferences from the API
 */
const fetchNotificationPreferences = async () => {
  const response = await fetch('/api/notifications');

  if (!response.ok) {
    throw new Error(
      `Failed to fetch notification preferences: ${response.status} ${response.statusText}`
    );
  }

  return await response.json();
};

/**
 * Common hook to determine whether a user has set notification preferences.
 * This is used by both the guard hook and the header visibility logic.
 */
const useNotificationPreferences = () => {
  const isFeatureEnabled = useNotificationPreferencesFeatureFlag();

  const { user } = useAuth0User();

  const wordpressId = user?.wordpressId?.toString();
  const registeredAt: any = user?.registeredAt;

  const isNewUser = registeredAt
    ? dayjs(registeredAt).isAfter(
        dayjs(new Date(process.env.NEXT_PUBLIC_NEW_USER_DATE ?? '2025-05-15'))
      )
    : false;

  const preferencesQuery = useQuery(
    ['notificationPreferences', wordpressId],
    fetchNotificationPreferences,
    {
      enabled: !!wordpressId && user && isNewUser,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1
    }
  );

  const { data, isLoading, isFetched } = preferencesQuery;

  const hasPreferences = isFetched ? !!(data && data.hasPreferences) : false;

  return {
    hasPreferences,
    isLoading,
    isFetched,
    isFeatureEnabled,
    isNewUser
  };
};

export default useNotificationPreferences;
